import { Hero } from "@/components/Hero";
import { CinematicHero } from "@/components/CinematicHero";
import { ReserveBand } from "@/components/ReserveBand";
import { FeatureCards } from "@/components/FeatureCards";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Feather, BatteryCharging, Droplets, Users, Video, MapPin, Phone, Navigation, Mic, ShieldAlert } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const stats = [
  { value: "Guided", label: "navigation with guardian alerts" },
  { value: "24/7", label: "live GPS tracking" },
  { value: "1-tap", label: "emergency SOS & 911" },
  { value: "2-way", label: "live voice with guardians" },
];

const appFeatures = [
  { icon: Video, title: "Live Stream", desc: "See and hear their world in real time." },
  { icon: MapPin, title: "Location", desc: "Live tracking on an in-app map." },
  { icon: Phone, title: "Voice Call", desc: "Tap to talk, instantly." },
  { icon: Navigation, title: "Take Me Home", desc: "Guided navigation home." },
  { icon: Mic, title: "Voice Command", desc: "Hands-free, just say it." },
  { icon: ShieldAlert, title: "Emergency SOS", desc: "One tap to call 911." },
];

const everyday = [
  { icon: Feather, title: "Featherlight frames", desc: "Premium acetate frames — lightweight and comfortable to wear all day." },
  { icon: BatteryCharging, title: "All-day battery", desc: "Up to 8 hours per charge with fast top-ups in the case." },
  { icon: Droplets, title: "Water-resistant", desc: "IPX5 rated to shrug off rain, splashes and sweat." },
  { icon: Users, title: "Fits kids & adults", desc: "Two tailored sizes with a secure, all-day comfortable fit." },
];

const testimonials = [
  { quote: "I finally stopped worrying on my son's walk to school.", name: "Maria G.", role: "Parent of two" },
  { quote: "Mom kept her independence, and we kept our peace of mind.", name: "David R.", role: "Family caregiver" },
  { quote: "The SOS button gave our whole family real confidence.", name: "The Alvarez Family", role: "Vsense members" },
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      <CinematicHero />

      <ReserveBand />

      <Hero />

      {/* Feature cards */}
      <FeatureCards />

      {/* Stats — WHOOP-style bold metrics on dark */}
      <section data-testid="stats-section" className="bg-zinc-950 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter max-w-3xl">
              Worn every day. Safe every day.
            </h2>
            <p className="mt-4 text-base md:text-lg text-white/60 max-w-xl">
              Vsense turns a stylish pair of glasses into an always-on safety companion for the people you love.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-5">
                  <div className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter">{s.value}</div>
                  <div className="mt-2 text-sm md:text-base text-white/55">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* App showcase — dark, real app screens */}
      <section id="features" data-testid="app-section" className="bg-zinc-950 text-white pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">The Vsense app</p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter leading-tight">
              Stay connected.<br />Stay confident.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/60 max-w-md mx-auto leading-relaxed">
              Real-time protection and peace of mind for your loved ones — everything lives
              in one beautifully simple guardian app.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 md:mt-16">
            <motion.img
              initial={{ scale: 0.97, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease }}
              src="/products/app-phones.jpg"
              alt="The Vsense guardian app showing live stream and live location"
              className="w-full rounded-3xl"
            />
          </Reveal>

          <div className="mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 max-w-4xl mx-auto">
            {appFeatures.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-semibold">{f.title}</div>
                  <div className="text-sm text-white/50">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <button
              data-testid="explore-app-btn"
              onClick={() => navigate("/app")}
              className="group inline-flex items-center gap-2 bg-white text-zinc-900 text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-zinc-200 active:scale-[0.98] transition-all duration-200"
            >
              Explore the app
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Parent watching block */}
      <section data-testid="parent-section" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal className="md:order-2">
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-4">Peace of mind, anywhere</p>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter text-zinc-900 leading-tight">
              Watch over them,<br />from anywhere.
            </h2>
            <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed max-w-md">
              A quick glance is all it takes. Check their live location, tap to talk, or start
              a live stream — and know the ones you love are safe, right now.
            </p>
            <div className="mt-8 flex flex-col gap-3">
              {["See live location the moment you open the app", "Two-way voice with a single tap", "Instant alerts the second an SOS is triggered"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-zinc-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-900" />
                  <span className="text-sm md:text-base">{t}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="md:order-1">
            <div className="relative rounded-[2rem] overflow-hidden bg-[#F5F5F7] aspect-[4/3] shadow-[0_24px_60px_rgb(0,0,0,0.08)]">
              <img src="/products/parent-app.jpg" alt="A parent checking on their child in the Vsense app" className="w-full h-full object-cover" />
              <div className="absolute bottom-5 left-5 flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/70 backdrop-blur-xl border border-white/60 shadow-lg">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500/40" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-zinc-900">Ethan · Online</div>
                  <div className="text-xs text-zinc-500">Live location active</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-[2.5rem] bg-zinc-900 text-white px-8 py-16 md:px-16 md:py-24 text-center">
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter">
                Two models. One promise.
              </h2>
              <p className="mt-4 text-base md:text-lg text-zinc-300 max-w-lg mx-auto">
                Compare Vsense Kids and Vsense Adult and choose the perfect fit to keep your loved ones safe.
              </p>
              <button
                data-testid="cta-shop-btn"
                onClick={() => navigate("/shop")}
                className="mt-8 inline-flex items-center gap-2 bg-white text-zinc-900 text-sm font-medium px-7 py-3.5 rounded-full hover:bg-zinc-100 active:scale-[0.98] transition-all duration-200"
              >
                Reserve Your Pair
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
